import { useCallback, useMemo } from 'react';
import {
    Combobox,
    ComboboxList,
    ComboboxOption,
    ComboboxOptionTextContainer,
    ComboboxOptionThumbnailContainer,
    ComboboxPopover,
    ComboboxSearchInput,
    FlexBox,
    FluidImage,
    Spinner,
} from '@vp/swan';
import { SupportedLocale } from '@99designs/i18n';
import { sendProductSearchResultClickedTrackingEvent, usePage } from '@99designs/tracking';
import { getPopularDesignSearchProducts } from '../../../products/getPopularDesignSearchProducts';
import { ComboBoxResultsHeader } from '../ComboBoxResultsHeader';
import { HighlightedText } from '../HighlightedText';
import { getProductSearchTrackingParams, trackPopoverVisibility } from './helpers';
import type { DesignProductSearchResult } from '../../../types/Search';

interface DesignProductSearchProps {
    locale: SupportedLocale;
    onInputChange: ((value: string) => void) | undefined;
    inputValue: string;
    searchResults: DesignProductSearchResult[];
    loading: boolean;
    onPopoverOpen: (isPopoverOpen: boolean) => void;
    minSearchQueryLength?: number;
}

export const DesignProductSearch = ({
    locale,
    onInputChange,
    inputValue,
    searchResults,
    loading,
    minSearchQueryLength = 0,
    onPopoverOpen,
}: DesignProductSearchProps) => {
    const { pageData } = usePage();
    const popularDesignProductResults = useMemo(
        () => getPopularDesignSearchProducts(locale),
        [locale]
    );
    const resultItems = searchResults.length > 0 ? searchResults : popularDesignProductResults;

    // This is required to set the max height of the popover allowing us to fit 5 results instead of 3
    // The SWAN team is currently looking into exposing this as a prop instead
    const setComboListRef = (node: HTMLUListElement) => {
        if (node) {
            node.style.maxHeight = '400px';
        }
    };

    const setComboPopoverRef = useCallback(
        (node: HTMLDivElement | null) => trackPopoverVisibility(node, onPopoverOpen),
        [onPopoverOpen]
    );

    const handleSelectionChange = (key: string | number) => {
        if (!key) return;

        const position = resultItems.findIndex(
            (result) => result.designProductPresenter.key === key
        );
        const selectedProduct = resultItems[position];

        if (selectedProduct) {
            window.location.assign(selectedProduct.designProductPresenter.ctaUrl(locale));
            sendProductSearchResultClickedTrackingEvent({
                ...pageData,
                ...getProductSearchTrackingParams({
                    inputValue,
                    selectedProduct,
                    position,
                    resultLength: resultItems.length,
                    locale,
                }),
            });
        }
    };

    return (
        <Combobox
            aria-label="Design Service Product Search"
            allowsCustomValue
            allowEmptyCollection
            menuTrigger="focus"
            onInputChange={onInputChange}
            inputValue={inputValue}
            items={resultItems}
            onSelectionChange={handleSelectionChange}
        >
            <ComboboxSearchInput
                placeholder="I'm looking for (e.g. logo, postcard...)"
                accessibleTextForSearchButton="Search for design service products"
                accessibleTextForClearButton="Clear results"
            />
            <ComboboxPopover ref={setComboPopoverRef}>
                {loading && (
                    <FlexBox
                        justifyContent="center"
                        alignItems="center"
                        style={{ height: '300px' }}
                    >
                        <Spinner
                            accessibleText="Gathering the best results for you"
                            role="status"
                            size="super"
                            layout="vertical"
                            showText
                            fontSkin="title-item"
                            style={{ maxWidth: '100%' }}
                        />
                    </FlexBox>
                )}
                {!loading && (
                    <>
                        <ComboBoxResultsHeader
                            hasSearched={inputValue.length >= minSearchQueryLength}
                            hasResults={searchResults.length > 0}
                        />
                        <ComboboxList ref={setComboListRef}>
                            {(result: DesignProductSearchResult) => (
                                <ComboboxOption
                                    key={result.designProductPresenter.key}
                                    textValue={result.designProductPresenter.translatedTitle()}
                                    withThumbnail
                                >
                                    <ComboboxOptionThumbnailContainer>
                                        <FluidImage
                                            src={
                                                result.designProductPresenter.searchPreviewImage({
                                                    cloudinaryOptions: { width: 100 },
                                                })?.url
                                            }
                                            alt=""
                                            role="presentation"
                                            width={150}
                                        />
                                    </ComboboxOptionThumbnailContainer>
                                    <ComboboxOptionTextContainer>
                                        <HighlightedText
                                            text={result.matches[0].value}
                                            indices={result.matches[0].indices}
                                        />
                                    </ComboboxOptionTextContainer>
                                </ComboboxOption>
                            )}
                        </ComboboxList>
                    </>
                )}
            </ComboboxPopover>
        </Combobox>
    );
};
